import React, { useRef, useState, type ReactNode } from "react";
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles,
  type Placement,
  type ReferenceType,
} from "@floating-ui/react";
import { twMerge } from "tailwind-merge";

interface ITooltipProps {
  content: ReactNode;
  placement?: Placement;
  containerClassName?: string;
  children:
    | ReactNode
    | ((anchorProps: {
        ref: (node: ReferenceType | null) => void;
      }) => ReactNode);
}

export const Tooltip: React.FC<ITooltipProps> = ({
  content,
  children,
  containerClassName,
  placement = "top",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = useRef<SVGSVGElement>(null);

  const {
    refs: { setReference, setFloating },
    floatingStyles,
    context,
  } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(12),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const { styles: transitionStyles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
  });

  return (
    <React.Fragment>
      {typeof children === "function" ? (
        children({ ref: setReference, ...getReferenceProps() })
      ) : (
        <div
          ref={setReference}
          {...getReferenceProps()}
          className={twMerge("inline w-fit", containerClassName)}
        >
          {children}
        </div>
      )}
      {isOpen && content && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={{ ...floatingStyles, zIndex: 60 }}
            {...getFloatingProps()}
          >
            <div
              style={transitionStyles}
              className="rounded-lg bg-base-black px-3 py-2 text-xs font-medium leading-4 text-base-white opacity-100"
            >
              <FloatingArrow
                tipRadius={1}
                height={8}
                ref={arrowRef}
                context={context}
                className="fill-base-black"
              />
              <div className="max-w-80">{content}</div>
            </div>
          </div>
        </FloatingPortal>
      )}
    </React.Fragment>
  );
};
